import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { updateProductSchema, type UpdateProductFormData } from "@/lib/validationSchemas"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { ProductResponseDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toastSuccess, toastError } from "@/lib/toast"
import { ApiError } from "@/api/types"
import { ProductFields } from "./ProductFields"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Form } from "@/components/ui/form"
import { useLanguage } from "@/hooks/useLanguage"

interface EditProductSheetProps {
  product: ProductResponseDto | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const EditProductSheet: React.FC<EditProductSheetProps> = ({
  product,
  open,
  onOpenChange,
}) => {
  const queryClient = useQueryClient()
  const { t, language } = useLanguage()
  const text = t.productForm
  const [deletedImageIds, setDeletedImageIds] = React.useState<number[]>([])

  const form = useForm<UpdateProductFormData>({
    resolver: zodResolver(updateProductSchema),
  })

  const { handleSubmit, setError, reset, setValue } = form

  useEffect(() => {
    if (product && open) {
      setDeletedImageIds([])
      reset({
        name: product.name,
        sku: product.sku || "",
        description: product.description || "",
        price: product.price,
        unitCost: product.unitCost,
        weight: product.weight,
        weightUnit: product.weightUnit,
        lowStockThreshold: product.lowStockThreshold || 5,
        leadTimeDays: product.leadTimeDays || 0,
        safetyStockLevel: product.safetyStockLevel || 0,
        isTaxable: product.isTaxable,
        consumptionType: product.consumptionType || "Human",
        categoryId: product.categoryId || undefined,
        providerId: product.providerId || undefined,
        primaryImageIndex: 0,
        imageFiles: [],
        technicalSpecFile: [],
      })

      if (product.categoryId) {
        setValue("categoryId", product.categoryId as any)
      }
    }
  }, [product, open, reset, setValue])

  const handleDeleteImage = (id: number) => {
    setDeletedImageIds((prev) => [...prev, id])
  }

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProductFormData) =>
      apiClient.updateProduct(product!.id, {
        name: data.name,
        sku: data.sku || undefined,
        price: data.price,
        unitCost: data.unitCost,
        description: data.description,
        categoryId: data.categoryId || undefined,
        weight: data.weight,
        weightUnit: data.weightUnit,
        lowStockThreshold: data.lowStockThreshold,
        leadTimeDays: data.leadTimeDays,
        safetyStockLevel: data.safetyStockLevel,
        isTaxable: data.isTaxable,
        consumptionType: data.consumptionType,
        providerId: data.providerId || undefined,
        primaryImageIndex: data.primaryImageIndex,
        deletedImageIds: deletedImageIds,
        images: data.imageFiles && data.imageFiles.length > 0 ? data.imageFiles : undefined,
        technicalSpecification:
          data.technicalSpecFile && data.technicalSpecFile.length > 0
            ? data.technicalSpecFile[0]
            : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-products"] })
      onOpenChange(false)
      toastSuccess(text.successUpdate)
    },
    onError: (err: unknown) => {
      const error = err as ApiError
      if (error.response) {
        try {
          const errorData = JSON.parse(error.response)
          if (errorData.errors) {
            Object.keys(errorData.errors).forEach((key) => {
              const field = (key.charAt(0).toLowerCase() +
                key.slice(1)) as keyof UpdateProductFormData
              setError(field, { type: "manual", message: errorData.errors[key][0] })
            })
            toastError(text.validationError)
            return
          }
        } catch (e) {}
      }
      toastError(error.message || "Error updating product")
    },
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-background p-0 shadow-2xl sm:max-w-[700px]">
        <div className="flex h-full flex-col px-1">
          {" "}
          {/* Safety gutter */}
          <SheetHeader className="border-b bg-muted/5 p-6">
            <SheetTitle className="font-display text-2xl tracking-widest">{text.title}</SheetTitle>
            <div className="h-1 w-12 bg-primary"></div>
          </SheetHeader>
          <ScrollArea className="container flex-1">
            <div className="p-6">
              <Form {...form}>
                <form
                  id="edit-product-form"
                  onSubmit={handleSubmit((data) => updateMutation.mutate(data))}
                  className="space-y-8 pb-10"
                >
                  <ProductFields
                    register={form.register}
                    errors={form.formState.errors}
                    setValue={form.setValue}
                    watch={form.watch}
                    control={form.control}
                    translations={text}
                    readOnlyName={true}
                    language={language}
                    showFiles={true}
                    existingImages={product?.images?.filter(
                      (img) => !deletedImageIds.includes(img.id)
                    )}
                    existingTechSpec={product?.technicalSpecificationUrl || undefined}
                    onDeleteImage={handleDeleteImage}
                  />
                </form>
              </Form>
            </div>
          </ScrollArea>
          <SheetFooter className="border-t bg-muted/10 p-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="font-display tracking-widest"
            >
              {text.cancel}
            </Button>
            <Button
              type="submit"
              form="edit-product-form"
              disabled={updateMutation.isPending}
              className="bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
            >
              {updateMutation.isPending ? "..." : text.save}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}

